import React, { memo } from "react";

import { Button, Icon } from "common/components-v2";
import AsyncAvatar from "common/components/custom/async-avatar/AsyncAvatar";
import commonLabelCollection from "common/config/common-keys.config";
import { IconCollection } from "common/config/media.config";
import { ButtonType, ButtonVariant } from "common/enums";
import useCustomTranslation from "common/hooks/useCustomTranslation";
import { useAppSelector } from "common/redux/core/root.reducer";
import { toSafeObjectKey } from "common/utils/object";

import { ClientOptions } from "modules/home/enums/create-engagement/create-engagement.enum";
import { Client } from "modules/home/models/home-common.model";
import labelCollection from "../../config/langauge-keys.config";
import { getClientInitials } from "../../util/getClientInitials.util";
import ClientMoreOptions from "../client-more-options/ClientMoreOptions";

import "./ClientCard.scss";

type ClientCardProps = {
  clientData: Client;
  onOptionClick: (option: ClientOptions, data: Client) => void;
};

const ClientCard = React.forwardRef<HTMLDivElement, ClientCardProps>(
  ({ clientData, onOptionClick }, ref) => {
    const { t } = useCustomTranslation(["common", "home"]);

    const { id, name, clientCode, users, sectorName } = clientData;
    const { isSuperAdmin, id: currentUserid } = useAppSelector(
      (state) => state.user
    );
    const { clientLogos } = useAppSelector((state) => state.home);

    const clientLogo = clientLogos?.[toSafeObjectKey(id)] || "";
    const isCurrentUserPartOfClient = users?.find(
      (user) => user?.userId === currentUserid
    );

    return (
      <div className="client-card-v2" ref={ref} data-client-id={id}>
        <div className="client-card-v2__header">
          <div className="client-logo">
            {clientLogo ? (
              <img
                src={clientLogo ?? ""}
                className="profile-img"
                alt="Client Icon"
              />
            ) : (
              <p>
                <span>{getClientInitials(name)} </span>
              </p>
            )}
          </div>

          {isSuperAdmin && (
            <ClientMoreOptions
              clientData={clientData}
              onOptionClick={(option, data) => onOptionClick(option, data)}
            />
          )}
        </div>

        <div className="client-card-v2__body">
          <div className="client-info">
            <h4 title={name}>{name}</h4>
            <p>
              {t(labelCollection.id)}: {clientCode}
            </p>
            <p className="sector">
              {t(labelCollection.sector)}: <span>{sectorName || "--"}</span>
            </p>
          </div>
        </div>
        <div className="client-card-v2__footer">
          <div className="team">
            {users?.slice(0, 5)?.map((user, index: number) => (
              <AsyncAvatar
                id={user?.userId}
                name={user?.displayName}
                key={index}
              />
            ))}
            {users && users?.length > 5 && (
              <span className="total-count">+{(users.length ?? 0) - 5}</span>
            )}
            {users?.length === 0 && "--"}
          </div>

          <Button
            type={ButtonType.BUTTON}
            variant={ButtonVariant.PRIMARY}
            size="small"
            onClick={() => onOptionClick(ClientOptions.VIEW, clientData)}
            disabled={!isCurrentUserPartOfClient && !isSuperAdmin}
            aria-label={t(commonLabelCollection.view)}
          >
            {t(commonLabelCollection.view)}

            <Icon
              src={IconCollection.IC_ARROW_RIGHT}
              className="chevron-right-icon"
              stroke="none"
              strokeWidth=".01rem"
            />
          </Button>
        </div>
      </div>
    );
  }
);

export default memo(ClientCard);
